import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAudioSettings, playSfx, setAudioSettings, subscribeAudio, unlockAudio } from '@/lib/audio/sfx';

/**
 * Botón de sonido (🔊/🔇) con control de volumen desplegable. La preferencia
 * se guarda en el navegador. Pulsarlo también desbloquea el audio.
 */
export const SoundControl = ({ compact = false }: { compact?: boolean }) => {
  const settings = useSyncExternalStore(subscribeAudio, getAudioSettings, getAudioSettings);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [open]);

  const silent = settings.muted || settings.volume === 0;
  const icon = silent ? '🔇' : settings.volume < 0.4 ? '🔈' : '🔊';

  return (
    <div ref={box} className="pointer-events-auto relative">
      <button
        type="button"
        aria-label={silent ? 'Sonido desactivado' : 'Sonido activado'}
        aria-expanded={open}
        onClick={() => {
          unlockAudio();
          setOpen((v) => !v);
        }}
        className={`btn-ghost grid place-items-center ${compact ? 'h-9 w-9' : 'h-9 w-9'} text-base`}
      >
        {icon}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-56 rounded-xl border border-white/10 bg-ink-800/95 p-3 shadow-xl backdrop-blur">
          <div className="flex items-center justify-between text-xs text-slate-300">
            <span className="font-semibold text-white">Sonido</span>
            <button
              type="button"
              onClick={() => {
                setAudioSettings({ muted: !settings.muted });
                if (settings.muted) playSfx('whistle', 0.5);
              }}
              className={`rounded-md px-2 py-1 font-semibold ${settings.muted ? 'bg-red-500/20 text-red-200' : 'bg-emerald-500/20 text-emerald-200'}`}
            >
              {settings.muted ? 'Silenciado' : 'Activado'}
            </button>
          </div>
          <label className="mt-3 block text-[11px] text-slate-400">
            Volumen: {Math.round(settings.volume * 100)}%
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(settings.volume * 100)}
              onChange={(e) => setAudioSettings({ volume: Number(e.target.value) / 100, muted: false })}
              onPointerUp={() => playSfx('kick', 0.8)}
              className="mt-1 w-full accent-cyan-400"
              aria-label="Volumen"
            />
          </label>
        </div>
      )}
    </div>
  );
};
